/**
 * MIRROR of `packages/shared/src/constants/calibration-chips.ts`.
 *
 * Why a mirror and not an import: `packages/web/Dockerfile` copies ONLY
 * `packages/web` into the build image and runs a bare `npm install` there, so a
 * workspace dependency on `@recruiter-buddy/shared` cannot resolve and would
 * break the production web image. Every other package imports the shared copy
 * directly; this is the one consumer that structurally cannot.
 *
 * Drift guard: `packages/shared/src/constants/__tests__/calibration-chips.test.ts`
 * reads this file and asserts the ids, labels, polarities AND the optional
 * `retired` flag match the shared list exactly. If you edit one, edit both —
 * that test will fail otherwise.
 */

export type Verdict = 'match' | 'not_match' | 'unsure';
export type ChipPolarity = 'positive' | 'negative';

export interface CalibrationChip {
  id: string;
  label: string;
  polarity: ChipPolarity;
  /**
   * US-491. A retired chip is legacy-resolvable, not deleted: it stays here so
   * an already-recorded judgment still resolves to a label and polarity for
   * display, but `chipsForVerdict()` filters it out so the card never offers
   * it — and the judge route, which validates against the shared list, 400s it
   * on new writes.
   */
  retired?: true;
}

export const CALIBRATION_CHIPS: readonly CalibrationChip[] = [
  { id: 'wrong_seniority', label: 'Wrong seniority', polarity: 'negative', retired: true },
  { id: 'too_senior', label: 'Too senior', polarity: 'negative' },
  { id: 'too_junior', label: 'Too junior / not enough experience', polarity: 'negative' },
  { id: 'wrong_function', label: 'Right industry, wrong role', polarity: 'negative' },
  { id: 'wrong_industry', label: 'Wrong industry', polarity: 'negative' },
  { id: 'wrong_experience', label: 'Wrong experience', polarity: 'negative' },
  { id: 'location', label: 'Location', polarity: 'negative' },
  { id: 'comp_mismatch', label: 'Comp mismatch', polarity: 'negative' },
  { id: 'right_seniority', label: 'Right seniority', polarity: 'positive' },
  { id: 'right_industry', label: 'Right industry', polarity: 'positive' },
  { id: 'relevant_experience', label: 'Relevant experience', polarity: 'positive' },
  { id: 'strong_reach_out', label: 'Strong — reach out', polarity: 'positive' },
];

const NEGATIVE_CHIPS = CALIBRATION_CHIPS.filter((c) => c.polarity === 'negative' && !c.retired);
const POSITIVE_CHIPS = CALIBRATION_CHIPS.filter((c) => c.polarity === 'positive' && !c.retired);

export function chipsForVerdict(verdict: Verdict): readonly CalibrationChip[] {
  return verdict === 'match' ? POSITIVE_CHIPS : NEGATIVE_CHIPS;
}

export function polarityOf(chipId: string): ChipPolarity | null {
  return CALIBRATION_CHIPS.find((c) => c.id === chipId)?.polarity ?? null;
}
